import { useEffect, useRef, useState } from 'react'
import { PreviewConfirmModal } from 'components/dialog/ModalTemplate'
import {
  arrayEquals,
  filterEmptyChildNodes,
  getClipboardData,
  isEmptyObject,
  pasteAsText,
  premiumLinkRegex,
  talkChannelRegex,
} from 'utils'
import useValidator from 'utils/hooks/useValidator'
import classNames from 'classnames'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { fail, failWithErrorResponse, success } from 'features/toast/Toast'
import {
  hideAdGroupKeywordExtensionEdit,
  hidePreview,
  KeywordExtensionTypeNames,
  KeywordExtensionTypes,
  setExceptionKeywords,
  setKeywordExtensionYn,
  showPreview,
  updateAdGroupsKeywordExtension,
} from 'features/dashboard/operation/adGroupKeywordExtensionEdit/adGroupKeywordExtensionEditSlice'
import { Modal } from 'components/dialog/Modals'
import Table from 'components/table/Table'
import { refreshDashboard } from 'features/dashboard/dashboardCommonSlice'
import Loading from 'components/loading/Loading'
import RadioButton from 'components/form/RadioButton'
import ExceptionKeywordsTooltip from 'features/dashboard/operation/adGroupKeywordExtensionEdit/ExceptionKeywordsTooltip'
import FixedTable from 'components/table/FixedTable'
import { convertDangerouslyHTML } from '../../../ad/group/form/ExceptionKeywords'

const AdGroupKeywordExtensionEditModal = () => {
  const dispatch = useDispatch()
  const {
    type,
    visible,
    isSubmitting,
    previewVisible,
    isLoading,
    adGroups,
    loadingError,
    form: { keywordExtensionYn, exceptionKeywords },
    preview: {
      keywordExtensionYn: previewKeywordExtensionYn,
      exceptionKeywords: previewExceptionKeywords,
    },
  } = useSelector(
    ({ dashboard }) => dashboard.operation.adGroupKeywordExtensionEdit,
    shallowEqual
  )
  const [exceptionKeywordCount, setExceptionKeywordCount] = useState(
    exceptionKeywords.length
  )
  const [focused, setFocused] = useState(false)
  const inputRef = useRef()
  const { errors, validate } = useValidator(
    () => ({
      exceptionKeywords:
        (keywordExtensionYn === 'Y' &&
          (type === KeywordExtensionTypes.keywordEx
            ? exceptionKeywords.some(item => !premiumLinkRegex.test(item)) && (
                <>
                  제외키워드에 입력 불가능한 문자가 입력되어 있습니다.
                  <br />
                  (특수문자 일부, 한자 입력 불가능)
                </>
              )
            : exceptionKeywords.some(item => !talkChannelRegex.test(item)) && (
                <>
                  제외키워드에 입력 불가능한 문자가 입력되어 있습니다.
                  <br />
                  (한글, 영문, 숫자, 특수문자 일부 외 입력 불가)
                </>
              ))) ||
        (keywordExtensionYn === 'Y' &&
          (type === KeywordExtensionTypes.keywordEx
            ? exceptionKeywords.some(item => item.length > 50) &&
              '제외키워드는 1자이상 50자 이하여야 합니다.'
            : exceptionKeywords.some(
                item => item.length > 50 || item.length < 2
              ) && '제외키워드는 2자이상 50자 이하여야 합니다.')),
    }),
    [keywordExtensionYn, exceptionKeywords, type]
  )

  const handleExceptionKeywordsPaste = event => {
    event.preventDefault()
    const text = getClipboardData(event)
    pasteAsText(text)
  }
  const handleExceptionKeywordsInput = event => {
    const { childNodes } = event.target
    setExceptionKeywordCount(filterEmptyChildNodes(childNodes).length)
  }
  const handleExceptionKeywordsKeyDown = event => {
    const { childNodes } = event.target
    const count = filterEmptyChildNodes(childNodes).length
    setExceptionKeywordCount(count)
    if (count >= 100 && event.key === 'Enter') {
      event.preventDefault()
    }
  }
  const handleExceptionKeywordsFocus = () => {
    setFocused(true)
  }
  const handleExceptionKeywordsChange = event => {
    setFocused(false)
    const { childNodes } = event.target
    const textContents = [
      ...new Set(
        filterEmptyChildNodes(childNodes).map(text =>
          type === KeywordExtensionTypes.keywordEx
            ? text.toLowerCase().replace(/\s+/g, '')
            : text.replace(/\s+/g, '')
        )
      ),
    ].slice(0, 100)

    dispatch(setExceptionKeywords(textContents))
    event.target.innerHTML = convertDangerouslyHTML(textContents)
  }
  const handleLabelClick = () => {
    setTimeout(() => inputRef.current?.focus(), 0)
  }
  const handlePreview = async () => {
    try {
      await validate()
      if (isEmptyObject(errors)) {
        dispatch(showPreview())
      }
    } catch (ignore) {}
  }
  const handleConfirm = async ({ preventClose }) => {
    try {
      await validate()
      if (isEmptyObject(errors)) {
        const { error, payload } = await dispatch(
          updateAdGroupsKeywordExtension()
        )
        if (error) {
          preventClose()
          if (payload?.errorCode === 104007) {
            fail('삭제 값이 존재합니다.\n새로고침 후 다시 시도해 주세요.')
          } else {
            fail(
              `${KeywordExtensionTypeNames[type]} 변경사항을 적용할 수 없습니다.\n다시 시도해 주세요.`
            )
          }
        } else {
          dispatch(refreshDashboard())
          success(
            `${KeywordExtensionTypeNames[type]} 변경사항이 적용되었습니다.`
          )
        }
      }
    } catch (error) {
      preventClose()
    }
  }

  useEffect(() => {
    setExceptionKeywordCount(exceptionKeywords.length)
  }, [exceptionKeywords])
  useEffect(
    () => () => {
      dispatch(hideAdGroupKeywordExtensionEdit())
    },
    [dispatch]
  )
  useEffect(() => {
    if (!isEmptyObject(errors)) {
      dispatch(hidePreview())
    }
  }, [dispatch, errors])
  useEffect(() => {
    if (loadingError) {
      dispatch(hideAdGroupKeywordExtensionEdit())
      failWithErrorResponse(loadingError, {
        messageMap: {
          104007: '삭제된 값이 존재합니다.\n새로고침 후 다시 시도해 주세요.',
        },
      })
    }
  }, [dispatch, loadingError])

  const preview = previewVisible && isEmptyObject(errors)
  const changed =
    keywordExtensionYn !== previewKeywordExtensionYn ||
    !arrayEquals(exceptionKeywords, previewExceptionKeywords)

  return (
    <Modal
      on={visible}
      className={classNames('change_layer', {
        extend_layer: type === KeywordExtensionTypes.keywordEx,
      })}>
      <PreviewConfirmModal
        title={`${KeywordExtensionTypeNames[type]} 변경`}
        isSubmitting={isSubmitting}
        onPreview={handlePreview}
        onConfirm={handleConfirm}
        previewDisabled={!changed && preview}
        confirmDisabled={changed && preview}
        handleClose={() => dispatch(hideAdGroupKeywordExtensionEdit())}>
        <p className="desc_info">
          {type === KeywordExtensionTypes.keywordEx
            ? '광고그룹 하위에 등록된 키워드외에 연관된 키워드에 자동으로 광고를 노출시키는것을 의미합니다.'
            : '광고그룹 하위에 등록된 키워드를 2글자 이상 자모음 분절한 키워드에 광고를 노출시키기 위한 것을 의미합니다.'}
        </p>
        {isLoading ? (
          <Loading />
        ) : (
          <>
            <div className="group_inp">
              <RadioButton
                label="설정"
                checked={keywordExtensionYn === 'Y'}
                onValueChange={() => dispatch(setKeywordExtensionYn('Y'))}
              />
              <RadioButton
                label="미설정"
                checked={keywordExtensionYn !== 'Y'}
                onValueChange={() => dispatch(setKeywordExtensionYn('N'))}
              />
            </div>
          </>
        )}
        {keywordExtensionYn === 'Y' && (
          <div
            className={classNames({
              box_subcard: type === KeywordExtensionTypes.keywordEx,
              box_subcard2: type !== KeywordExtensionTypes.keywordEx,
              in_error: errors.exceptionKeywords,
            })}>
            <div className="tit_item">
              <strong className="tit_detail">제외 키워드 설정</strong>
            </div>
            <p className="desc_info">
              {type === KeywordExtensionTypes.keywordEx
                ? '키워드확장을 설정하신 경우, 아래 입력한 키워드에 광고가 미노출됩니다.'
                : '입력한 키워드는 사용자가 검색시 노출되지 않습니다.'}
            </p>
            <div className="keyword_inp">
              {type === KeywordExtensionTypes.keywordEx && (
                <strong className="tit_inp">키워드 확장 제외 키워드</strong>
              )}
              <div className="box_textarea">
                {focused === false && exceptionKeywords.length === 0 && (
                  <label
                    htmlFor="tfArea"
                    className="lab_txt"
                    onMouseDown={handleLabelClick}>
                    한 줄에 하나씩 입력하세요.
                  </label>
                )}
                <div
                  id="tfArea"
                  ref={inputRef}
                  className="tf_area"
                  contentEditable
                  dangerouslySetInnerHTML={{
                    __html: convertDangerouslyHTML(exceptionKeywords),
                  }}
                  onInput={handleExceptionKeywordsInput}
                  onPaste={handleExceptionKeywordsPaste}
                  onKeyDown={handleExceptionKeywordsKeyDown}
                  onFocus={handleExceptionKeywordsFocus}
                  onBlur={handleExceptionKeywordsChange}
                />
                <span className="txt_count">
                  <span className="txt_num">{exceptionKeywordCount}</span>/100
                </span>
              </div>
            </div>
          </div>
        )}
        {errors.exceptionKeywords && (
          <p className="txt_error">{errors.exceptionKeywords}</p>
        )}
        {preview && (
          <FixedTable caption={`${KeywordExtensionTypeNames[type]} 표`}>
            <Table.Head>
              <Table.Column
                title="선택 광고그룹"
                className="inner_cell"
                width={273}
              />
              <Table.Column
                title="변경 전"
                className="inner_cell"
                width={132}
              />
              <Table.Column title="변경 후" className="inner_cell" />
            </Table.Head>
            <Table.Body>
              {adGroups.map(
                ({
                  id,
                  name,
                  keywordExtensionYn: beforeYn,
                  exceptionKeywords: beforeKeywords,
                }) => (
                  <Table.Row
                    rowId={id}
                    key={id}
                    className={classNames({
                      update:
                        keywordExtensionYn !== beforeYn ||
                        !arrayEquals(beforeKeywords, previewExceptionKeywords),
                    })}>
                    <td>
                      <div className="inner_cell">
                        <span className="txt_limit2">{name}</span>
                      </div>
                    </td>
                    <td>
                      <div className="inner_cell">
                        {beforeYn === 'N' ? (
                          '미설정'
                        ) : (
                          <ExceptionKeywordsTooltip
                            exceptionKeywords={beforeKeywords}
                          />
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="inner_cell">
                        {previewKeywordExtensionYn === 'N' ? (
                          '미설정'
                        ) : (
                          <ExceptionKeywordsTooltip
                            exceptionKeywords={previewExceptionKeywords}
                          />
                        )}
                      </div>
                    </td>
                  </Table.Row>
                )
              )}
            </Table.Body>
          </FixedTable>
        )}
      </PreviewConfirmModal>
    </Modal>
  )
}
export default AdGroupKeywordExtensionEditModal
